import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Search, Filter, TrendingUp, TrendingDown, Coins, Shield, BarChart3 } from 'lucide-react';
import api from '../../lib/api';
import { TokenCard } from '../../components/tokenization/TokenCard';
import { ProofOfReserveBadge } from '../../components/tokenization/ProofOfReserveBadge';

interface MarketToken {
  id: string;
  name: string;
  symbol: string;
  category: string;
  issuerName: string;
  unitPrice: number;
  priceChange24h: number;
  totalSupply: number;
  availableSupply: number;
  marketCap: number;
  volume24h: number;
  yieldRate: number | null;
  maturityDate: string | null;
  porCoverage: number;
  minInvestment: number;
  imageUrl: string | null;
}

const categoryLabels: Record<string, string> = {
  STABLECOIN: 'Stablecoin',
  RECEIVABLE: 'Recebíveis',
  REAL_ESTATE: 'Imóveis',
  FUND_SHARE: 'Cotas de Fundo',
  FIXED_INCOME: 'Renda Fixa',
  COMMODITY: 'Commodities',
  PRECATORY: 'Precatórios',
};

export function TokenMarketplace() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [sortBy, setSortBy] = useState('volume');

  const { data: tokens, isLoading } = useQuery({
    queryKey: ['marketplace-tokens', search, categoryFilter, sortBy],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (categoryFilter) params.append('category', categoryFilter);
      params.append('sortBy', sortBy);
      const res = await api.get(`/tokenization/marketplace?${params}`);
      return res.data as MarketToken[];
    },
  });

  const { data: stats } = useQuery({
    queryKey: ['marketplace-stats'],
    queryFn: async () => {
      const res = await api.get('/tokenization/marketplace/stats');
      return res.data;
    },
  });

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value / 100);
  };

  const formatCompact = (value: number) => {
    if (value >= 1000000000) return `R$ ${(value / 100000000000).toFixed(1)}B`;
    if (value >= 1000000) return `R$ ${(value / 100000000).toFixed(1)}M`;
    if (value >= 1000) return `R$ ${(value / 100000).toFixed(1)}K`;
    return formatCurrency(value);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Marketplace de Tokens</h1>
        <p className="text-surface-400">Invista em ativos tokenizados com segurança</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-surface-900 rounded-xl border border-surface-800 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-500/20 rounded-lg">
              <Coins className="w-5 h-5 text-primary-400" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats?.totalTokens || 0}</p>
              <p className="text-sm text-surface-400">Tokens Disponíveis</p>
            </div>
          </div>
        </div>

        <div className="bg-surface-900 rounded-xl border border-surface-800 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-500/20 rounded-lg">
              <BarChart3 className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold">{formatCompact(stats?.totalMarketCap || 0)}</p>
              <p className="text-sm text-surface-400">Market Cap Total</p>
            </div>
          </div>
        </div>

        <div className="bg-surface-900 rounded-xl border border-surface-800 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/20 rounded-lg">
              <TrendingUp className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold">{formatCompact(stats?.volume24h || 0)}</p>
              <p className="text-sm text-surface-400">Volume 24h</p>
            </div>
          </div>
        </div>

        <div className="bg-surface-900 rounded-xl border border-surface-800 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-500/20 rounded-lg">
              <Shield className="w-5 h-5 text-yellow-400" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats?.avgPorCoverage?.toFixed(1) || 100}%</p>
              <p className="text-sm text-surface-400">Cobertura PoR Média</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar tokens..."
              className="w-full pl-10 pr-4 py-2 bg-surface-800 border border-surface-700 rounded-lg"
            />
          </div>
        </div>

        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="px-4 py-2 bg-surface-800 border border-surface-700 rounded-lg"
        >
          <option value="">Todas as Categorias</option>
          {Object.entries(categoryLabels).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>

        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="px-4 py-2 bg-surface-800 border border-surface-700 rounded-lg"
        >
          <option value="volume">Maior Volume</option>
          <option value="marketCap">Maior Market Cap</option>
          <option value="yield">Maior Rendimento</option>
          <option value="price">Menor Preço</option>
          <option value="newest">Mais Recentes</option>
        </select>
      </div>

      {/* Token Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : !tokens?.length ? (
        <div className="text-center py-12 bg-surface-900 rounded-xl border border-surface-800">
          <Coins className="w-12 h-12 text-surface-600 mx-auto mb-3" />
          <p className="text-surface-400">Nenhum token encontrado</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {tokens.map((token) => (
            <div
              key={token.id}
              onClick={() => navigate(`/banking/tokens/${token.id}`)}
              className="bg-surface-900 rounded-xl border border-surface-800 p-4 hover:border-primary-500/50 transition-colors cursor-pointer"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  {token.imageUrl ? (
                    <img src={token.imageUrl} alt={token.name} className="w-10 h-10 rounded-full" />
                  ) : (
                    <div className="w-10 h-10 bg-primary-500/20 rounded-full flex items-center justify-center">
                      <Coins className="w-5 h-5 text-primary-400" />
                    </div>
                  )}
                  <div>
                    <p className="font-semibold">{token.name}</p>
                    <p className="text-sm text-surface-400">{token.symbol}</p>
                  </div>
                </div>
                <ProofOfReserveBadge coverage={token.porCoverage} size="sm" />
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex justify-between text-sm">
                  <span className="text-surface-400">Preço</span>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{formatCurrency(token.unitPrice)}</span>
                    <span className={`flex items-center text-xs ${
                      token.priceChange24h >= 0 ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {token.priceChange24h >= 0 ? (
                        <TrendingUp className="w-3 h-3 mr-0.5" />
                      ) : (
                        <TrendingDown className="w-3 h-3 mr-0.5" />
                      )}
                      {Math.abs(token.priceChange24h).toFixed(2)}%
                    </span>
                  </div>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-surface-400">Volume 24h</span>
                  <span>{formatCompact(token.volume24h)}</span>
                </div>
                {token.yieldRate && (
                  <div className="flex justify-between text-sm">
                    <span className="text-surface-400">Rendimento</span>
                    <span className="text-green-400">{token.yieldRate.toFixed(2)}% a.a.</span>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between pt-3 border-t border-surface-800">
                <span className="text-xs text-surface-400">
                  {categoryLabels[token.category] || token.category}
                </span>
                <span className="text-xs text-surface-400">
                  Min: {formatCurrency(token.minInvestment)}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default TokenMarketplace;
